<script lang="ts" setup>
import { ref } from 'vue';

import { useI18n } from '@/i18n/useI18n';

const { trans } = useI18n();

const relocate = function () {
	var win = window.open(
		'https://content.wepik.com/media/ai-presentations/Guide-to-Writing-Effective-Prompts-for-an-AI-Presentation-Maker.pdf?utm_source=ebook&utm_medium=pdf&utm_campaign=download-guide-prompts-ai-presentation-maker',
		'_blank'
	);

	if (win) {
		win.focus();
	}
};
</script>

<template>
	<div
		class="ai-loading z-50 flex w-full max-w-screen-sm cursor-wait flex-col items-center justify-center gap-4 rounded-md bg-white px-6 py-4"
	>
		<div>
			<div class="mb-4">
				<h3 class="mb-1 text-center text-2xl font-bold text-gray-800">
					{{ trans('Hold on, we’re generating your presentation about') }}
				</h3>
			</div>
			<div class="relative scale-75">
				<svg class="spinner m-auto block h-44 w-44" viewBox="0 0 96 96">
					<defs>
						<linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
							<stop offset="0%" stop-color="#1273eb" />
							<stop offset="100%" stop-color="#5f5ff6" />
						</linearGradient>
					</defs>
					<circle cx="48" cy="48" r="44" stroke="#CFD9E0" stroke-width="2" fill="transparent"></circle>
					<circle id="circle-animate" cx="48" cy="48" r="44" stroke="url(#gradient)"></circle>
				</svg>
				<div class="magic-wand absolute inset-0 flex items-center justify-center">
					<svg class="magic-wand h-16 w-16 overflow-visible" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
						<path
							class="wand"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M34.4956 41.695L6.54373 69.6469L10.2811 73.3842L38.2329 45.4324L34.4956 41.695ZM43.1148 47.0943L11.917 78.292C11.0135 79.1955 9.54864 79.1955 8.64514 78.292L1.63594 71.2828C0.732438 70.3793 0.732438 68.9145 1.63594 68.0109L32.8596 36.7872C32.8682 36.7787 32.8769 36.7702 32.8855 36.7618L46.2415 23.4058C47.145 22.5023 48.6098 22.5023 49.5133 23.4058L56.5225 30.415C57.426 31.3185 57.426 32.7834 56.5225 33.6869L43.1409 47.0685C43.1323 47.0772 43.1235 47.0857 43.1148 47.0943ZM41.505 42.1607L51.6147 32.051L47.8774 28.3136L37.7677 38.4233L41.505 42.1607Z"
							fill="url(#gradient)"
						/>
						<path
							class="star star--1"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M60.3538 1.33338e-10C61.2244 -9.35179e-06 62.0203 0.491919 62.4096 1.27066L64.9386 6.32978L69.9977 8.85866C70.7764 9.24793 71.2684 10.0438 71.2684 10.9144C71.2684 11.785 70.7765 12.5809 69.9977 12.9702L64.9386 15.4993L62.4096 20.5584C62.0203 21.3371 61.2244 21.8291 60.3538 21.8291C59.4831 21.829 58.6873 21.3371 58.298 20.5584L55.7691 15.4992L50.71 12.9702C49.9312 12.5809 49.4393 11.785 49.4393 10.9144C49.4393 10.0438 49.9313 9.24793 50.71 8.85866L55.7691 6.32979L58.298 1.2707C58.6873 0.491954 59.4831 9.35205e-06 60.3538 1.33338e-10ZM60.3538 7.43853L59.538 9.07055C59.3156 9.51551 58.9548 9.8763 58.5099 10.0987L56.8779 10.9145L58.5099 11.7304C58.9549 11.9528 59.3156 12.3136 59.538 12.7585L60.3538 14.3905L61.1697 12.7585C61.3921 12.3135 61.7529 11.9528 62.1978 11.7304L63.8299 10.9145L62.1978 10.0987C61.7529 9.8763 61.3921 9.51553 61.1697 9.07059L60.3538 7.43853Z"
							fill="url(#gradient)"
						/>
						<path
							class="star star--2"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M23.0764 7.3883C23.955 7.3883 24.7581 7.88463 25.1511 8.6704L28.1185 14.6041L34.0523 17.5716C34.838 17.9645 35.3344 18.7677 35.3344 19.6462C35.3344 20.5248 34.838 21.3279 34.0523 21.7209L28.1185 24.6883L25.1511 30.6219C24.7581 31.4077 23.955 31.904 23.0764 31.904C22.1979 31.904 21.3948 31.4077 21.0018 30.6219L18.0344 24.6883L12.1007 21.7209C11.315 21.3279 10.8187 20.5248 10.8187 19.6462C10.8187 18.7677 11.315 17.9645 12.1007 17.5716L18.0344 14.6042L21.0018 8.6704C21.3947 7.88463 22.1979 7.3883 23.0764 7.3883ZM23.0764 14.894L21.8379 17.3706C21.6135 17.8193 21.2496 18.1832 20.8008 18.4077L18.3242 19.6462L20.8008 20.8848C21.2496 21.1092 21.6135 21.4731 21.8379 21.9219L23.0764 24.3984L24.315 21.9219C24.5394 21.4731 24.9033 21.1092 25.3521 20.8848L27.8287 19.6462L25.3521 18.4077C24.9033 18.1833 24.5394 17.8194 24.315 17.3706L23.0764 14.894Z"
							fill="url(#gradient)"
						/>
						<path
							class="star star--3"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M65.2235 36.2698C66.1042 36.2698 66.9093 36.7618 67.3031 37.5406L71.1066 45.0624L78.7147 48.8226C79.5024 49.2119 80 50.0079 80 50.8786C80 51.7493 79.5024 52.5452 78.7147 52.9345L71.1066 56.6948L67.3031 64.2164C66.9093 64.9952 66.1042 65.4872 65.2235 65.4872C64.3428 65.4872 63.5377 64.9952 63.1439 64.2164L59.3404 56.6948L51.7322 52.9345C50.9445 52.5452 50.4468 51.7493 50.4468 50.8786C50.4468 50.0079 50.9445 49.2119 51.7322 48.8226L59.3404 45.0624L63.1439 37.5406C63.5377 36.7618 64.3428 36.2698 65.2235 36.2698ZM65.2235 43.7088L63.153 47.8034C62.928 48.2484 62.5631 48.6092 62.113 48.8316L57.9713 50.8786L62.113 52.9255C62.5631 53.148 62.928 53.5087 63.153 53.9537L65.2235 58.0483L67.294 53.9537C67.519 53.5088 67.8839 53.148 68.334 52.9256L72.4756 50.8786L68.334 48.8316C67.8839 48.6092 67.519 48.2484 67.294 47.8034L65.2235 43.7088Z"
							fill="url(#gradient)"
						/>
						<path
							class="star star--4"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M75.1304 22.1649C76.336 22.1649 77.3133 23.2176 77.3133 24.5161V29.5528C77.3133 30.8513 76.336 31.904 75.1304 31.904C73.9248 31.904 72.9475 30.8513 72.9475 29.5528V24.5161C72.9475 23.2176 73.9248 22.1649 75.1304 22.1649Z"
							fill="url(#gradient)"
						/>
						<path
							class="star star--4"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M70.2609 27.2024C70.2609 25.904 71.3135 24.8515 72.6121 24.8515H77.6488C78.9473 24.8515 80 25.904 80 27.2024C80 28.5007 78.9473 29.5532 77.6488 29.5532H72.6121C71.3135 29.5532 70.2609 28.5007 70.2609 27.2024Z"
							fill="url(#gradient)"
						/>
						<defs>
							<linearGradient
								id="gradient"
								x1="0.958316"
								y1="-5.72104"
								x2="87.4502"
								y2="70.2494"
								gradientUnits="userSpaceOnUse"
							>
								<stop stop-color="#0161CD" />
								<stop offset="1" stop-color="#8080F1" />
							</linearGradient>
						</defs>
					</svg>
				</div>
				<div class="typewriter absolute inset-0 flex items-center justify-center">
					<!--Typewriter-->
					<svg
						class="h-16 w-16 overflow-visible"
						viewBox="0 0 80 80"
						fill="url(#gradient)"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							class="ribbon"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M77.6561 18.7915C76.3615 18.7915 75.3123 19.8414 75.3123 21.1353V28.8336H66.1925H4.68749V28.667C4.68749 27.3725 3.6383 26.3233 2.34375 26.3233C1.04919 26.3233 0 27.3725 0 28.667V43.7292C0 45.0238 1.04919 46.073 2.34375 46.073C3.6383 46.073 4.68749 45.0238 4.68749 43.7292V43.5626H10.0415H14.729H65.2709H69.9584H77.6561C78.9507 43.5626 79.9998 42.5134 79.9998 41.2189V21.1353C79.9998 19.8414 78.9507 18.7915 77.6561 18.7915ZM4.6875 33.5211V38.8751H10.0415H13.8074V36.3648V33.5211H4.6875ZM69.9584 38.8751H75.3124V33.5211H66.1925V36.3648V38.8751H69.9584Z"
						/>
						<g class="papper">
							<path
								d="M66.1925 23.6463V28.8336V56H13.8074V28.8336V23.6463C13.8074 22.3517 14.8566 21.3025 16.1511 21.3025H18.8281H61.1718H63.8488C65.1433 21.3025 66.1925 22.3517 66.1925 23.6463Z"
							/>
							<path d="M18 26H62V52H18V26Z" fill="white" />
							<path
								d="M31.2134 35.0315H48.7866C50.0811 35.0315 51.1303 33.9817 51.1303 32.6878C51.1303 31.3932 50.0811 30.344 48.7866 30.344H31.2134C29.9194 30.344 28.8696 31.3932 28.8696 32.6878C28.8696 33.9817 29.9188 35.0315 31.2134 35.0315Z"
							/>
							<path
								d="M31.3437 41.6875H48.917C50.2115 41.6875 51.2607 40.6377 51.2607 39.3437C51.2607 38.0492 50.2115 37 48.917 37H31.3437C30.0498 37 29 38.0492 29 39.3437C29 40.6377 30.0492 41.6875 31.3437 41.6875Z"
							/>
							<path
								d="M31.3437 48.6875H48.917C50.2115 48.6875 51.2607 47.6377 51.2607 46.3437C51.2607 45.0492 50.2115 44 48.917 44H31.3437C30.0498 44 29 45.0492 29 46.3437C29 47.6377 30.0492 48.6875 31.3437 48.6875Z"
							/>
						</g>
						<g class="keyboard">
							<path
								d="M10.0415 40.788V38.5626V33.7085C10.0415 32.414 11.0913 31.3648 12.3852 31.3648H13.8073H22.4267C23.7213 31.3648 24.7705 32.414 24.7705 33.7085C24.7705 36.5686 27.0977 38.8959 29.9584 38.8959H50.0438C52.9113 38.8959 55.2294 36.5747 55.2294 33.7109C55.2294 32.4164 56.2786 31.3648 57.5731 31.3648H61.505H67.6146C68.9086 31.3648 69.9584 32.414 69.9584 33.7085V40.7886L79.8326 65.4736C79.9431 65.7501 79.9998 66.0461 79.9998 66.3439C79.9998 71.7901 75.5711 76.2188 70.1274 76.2188H9.87242C4.4287 76.2188 0 71.7901 0 66.3464C0 66.0479 0.0567625 65.7501 0.167846 65.4736L10.0415 40.788Z"
							/>
							<path
								d="M75.2965 66.7553C75.0871 69.4238 72.849 71.5313 70.1274 71.5313H9.87243C7.15087 71.5313 4.91272 69.4238 4.70337 66.7553L14.5617 42.11C14.6722 41.8335 14.729 41.5381 14.729 41.2396V36.0522H20.3643C21.419 40.3699 25.3204 43.5834 29.9584 43.5834H50.0439C56.4171 43.5834 59.0703 38.4009 59.6386 36.0522H65.2709V41.2396C65.2709 41.5381 65.3276 41.8335 65.4387 42.11L75.2965 66.7553Z"
								fill="white"
							/>
							<path
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M31.2134 51.1145H28.703C27.4084 51.1145 26.3593 50.0653 26.3593 48.7707C26.3593 47.4762 27.4084 46.427 28.703 46.427H31.2134C32.5079 46.427 33.5571 47.4762 33.5571 48.7707C33.5571 50.0653 32.5079 51.1145 31.2134 51.1145ZM14.8956 61.4898H17.406C18.7005 61.4898 19.7497 62.539 19.7497 63.8336C19.7497 65.1281 18.7005 66.1773 17.406 66.1773H14.8956C13.6017 66.1773 12.5519 65.1281 12.5519 63.8336C12.5519 62.539 13.6017 61.4898 14.8956 61.4898ZM26.1926 58.6462C27.4872 58.6462 28.5364 57.5964 28.5364 56.3025C28.5364 55.0079 27.4872 53.9587 26.1926 53.9587H23.6822C22.3877 53.9587 21.3385 55.0079 21.3385 56.3025C21.3385 57.5964 22.3877 58.6462 23.6822 58.6462H26.1926ZM21.1719 46.427C22.4664 46.427 23.5156 47.4762 23.5156 48.7707C23.5156 50.0653 22.4664 51.1145 21.1719 51.1145H18.6615C17.3669 51.1145 16.3177 50.0653 16.3177 48.7707C16.3177 47.4762 17.3669 46.427 18.6615 46.427H21.1719ZM38.745 51.1145H41.2548C42.5493 51.1145 43.5985 50.0653 43.5985 48.7707C43.5985 47.4762 42.5493 46.427 41.2548 46.427H38.745C37.4504 46.427 36.4012 47.4762 36.4012 48.7707C36.4012 50.0653 37.4504 51.1145 38.745 51.1145ZM33.7236 58.6462C32.4297 58.6462 31.3799 57.5964 31.3799 56.3025C31.3799 55.0079 32.4297 53.9587 33.7242 53.9587H36.234C37.5286 53.9587 38.5777 55.0079 38.5777 56.3025C38.5777 57.5964 37.5286 58.6462 36.234 58.6462H33.7236ZM48.7865 51.1145H51.2969C52.5914 51.1145 53.6406 50.0653 53.6406 48.7707C53.6406 47.4762 52.5914 46.427 51.2969 46.427H48.7865C47.4919 46.427 46.4427 47.4762 46.4427 48.7707C46.4427 50.0653 47.4919 51.1145 48.7865 51.1145ZM43.7657 58.6462C42.4712 58.6462 41.422 57.5964 41.422 56.3025C41.422 55.0079 42.4712 53.9587 43.7657 53.9587H46.2755C47.5701 53.9587 48.6192 55.0079 48.6192 56.3025C48.6192 57.5964 47.5701 58.6462 46.2755 58.6462H43.7657ZM63.6821 48.7707C63.6821 47.4762 62.6329 46.427 61.3384 46.427H58.828C57.5334 46.427 56.4843 47.4762 56.4843 48.7707C56.4843 50.0653 57.5334 51.1145 58.828 51.1145H61.3384C62.6329 51.1145 63.6821 50.0653 63.6821 48.7707ZM53.8072 53.9587H56.3176C57.6122 53.9587 58.6614 55.0079 58.6614 56.3025C58.6614 57.5964 57.6122 58.6462 56.3176 58.6462H53.8072C52.5127 58.6462 51.4635 57.5964 51.4635 56.3025C51.4635 55.0079 52.5127 53.9587 53.8072 53.9587ZM62.5939 61.4898C61.2993 61.4898 60.2501 62.539 60.2501 63.8336C60.2501 65.1281 61.2993 66.1773 62.5939 66.1773H65.1036C66.3982 66.1773 67.4474 65.1281 67.4474 63.8336C67.4474 62.539 66.3982 61.4898 65.1036 61.4898H62.5939ZM24.9377 61.4898H55.0622C56.3567 61.4898 57.4059 62.539 57.4059 63.8336C57.4059 65.1281 56.3567 66.1773 55.0622 66.1773H24.9377C23.6432 66.1773 22.594 65.1281 22.594 63.8336C22.594 62.539 23.6432 61.4898 24.9377 61.4898Z"
							/>
						</g>
						<defs>
							<linearGradient
								id="gradient"
								x1="0.958316"
								y1="-5.72104"
								x2="87.4502"
								y2="70.2494"
								gradientUnits="userSpaceOnUse"
							>
								<stop stop-color="#0161CD" />
								<stop offset="1" stop-color="#8080F1" />
							</linearGradient>
						</defs>
					</svg>
				</div>
				<div class="gallery absolute inset-0 flex items-center justify-center">
					<!--Images-->
					<svg
						class="h-16 w-16 overflow-visible"
						viewBox="0 0 93 80"
						fill="url(#gradient)"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							class="image"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M49.281 77.6563C49.281 78.9507 48.2316 80 46.9372 80C45.6428 80 44.5935 78.9507 44.5935 77.6563V69.8496C31.9202 68.6681 22 58.0028 22 45.0209C22 43.7265 23.0493 42.6771 24.3437 42.6771C32.6827 42.6771 40.0659 46.7704 44.5935 53.0573V39.7105C39.5076 39.176 34.7294 36.914 31.0804 33.264L31.0799 33.2634C26.8736 29.0583 24.5106 23.3535 24.5106 17.4063V2.34381C24.5106 1.39585 25.0816 0.541237 25.9574 0.178467C26.8332 -0.184303 27.8413 0.0162201 28.5116 0.686523L36.8959 9.07082L45.2803 0.686516C46.1956 -0.22876 47.6796 -0.22876 48.5949 0.686516L56.9793 9.07082L65.3636 0.686523C66.0339 0.0162201 67.042 -0.184303 67.9178 0.178467C68.7936 0.541237 69.3646 1.39585 69.3646 2.34381V17.4063C69.3646 23.3532 67.003 29.0582 62.7963 33.2637L62.796 33.264C59.1458 36.9141 54.3681 39.1763 49.281 39.7106V53.0557C53.8081 46.7697 61.1903 42.6771 69.5284 42.6771H69.5309C70.8254 42.6771 71.8747 43.7264 71.8747 45.0208C71.8747 58.0028 61.9543 68.6682 49.281 69.8496V77.6563ZM44.4414 65.1185C43.3119 55.9305 36.0278 48.6464 26.8398 47.5169C27.9693 56.705 35.2534 63.989 44.4414 65.1185ZM67.0349 47.5166C57.846 48.6451 50.5615 55.9296 49.433 65.1185C58.6212 63.9892 65.9055 56.7049 67.0349 47.5166ZM34.395 29.9494C37.721 33.2765 42.2335 35.1458 46.9376 35.1458C51.643 35.1458 56.1542 33.2766 59.4817 29.9492L59.482 29.9489C62.8088 26.6231 64.6771 22.1108 64.6771 17.4063V8.00211L58.6366 14.0426C57.7213 14.9579 56.2373 14.9579 55.322 14.0427L46.9376 5.65834L38.5531 14.0427C37.6378 14.9579 36.1539 14.9579 35.2386 14.0426L29.1981 8.00211V17.4063C29.1981 22.1104 31.0673 26.6229 34.3944 29.9489L34.395 29.9494Z"
						/>
						<path
							class="image image--1"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M2.02513 74.1563C1.34029 73.4719 0.998581 72.5744 1 71.6774L1 71.6718V8.50904C1 6.57105 2.567 5 4.5 5H88.5C90.433 5 92 6.57105 92 8.50904V71.6718V71.6761C92.0017 72.5736 91.66 73.4716 90.9749 74.1563C90.5036 74.6273 89.9314 74.9359 89.3278 75.0821C89.1186 75.1329 88.9021 75.165 88.6801 75.1762C88.6204 75.1793 88.5604 75.1808 88.5 75.1808H4.5C4.42237 75.1808 4.34533 75.1783 4.26895 75.1733C4.06595 75.16 3.86767 75.1294 3.67557 75.0829C3.07076 74.937 2.49727 74.6282 2.02513 74.1563ZM80.0278 68.1627H12.9721L25.5 55.6426L30.0251 60.1649C31.392 61.5309 33.608 61.5309 34.9749 60.1649L53.5 41.6512L80.0278 68.1627ZM55.9749 34.2311L85 63.2384V12.0181H8V63.2384L23.0251 48.2225C24.392 46.8565 26.608 46.8565 27.9749 48.2225L32.5 52.7449L51.0251 34.2311C52.392 32.8651 54.608 32.8651 55.9749 34.2311ZM22.0431 29.6809C22.0431 31.6282 23.5967 33.2067 25.513 33.2067C27.4293 33.2067 28.9828 31.6282 28.9828 29.6809C28.9828 27.7336 27.4293 26.1551 25.513 26.1551C23.5967 26.1551 22.0431 27.7336 22.0431 29.6809ZM25.513 40.2584C19.7641 40.2584 15.1034 35.5229 15.1034 29.6809C15.1034 23.8389 19.7641 19.1034 25.513 19.1034C31.2618 19.1034 35.9226 23.8389 35.9226 29.6809C35.9226 35.5229 31.2618 40.2584 25.513 40.2584Z"
						/>
						<path
							class="image image--2"
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M33.4337 10.8956C34.9329 10.8956 36.1483 9.68013 36.1483 8.18094V2.71461C36.1483 1.21542 34.9329 0 33.4337 0C31.9345 0 30.7191 1.21542 30.7191 2.71461V8.18094C30.7191 9.68013 31.9345 10.8956 33.4337 10.8956ZM8.8064 20.8323L4.0723 18.0991C2.77381 17.3491 1.11355 17.7943 0.364134 19.0926C-0.385462 20.3911 0.0593732 22.0513 1.35768 22.8008L6.09179 25.534C7.3872 26.2822 9.04873 25.8419 9.79995 24.5405C10.5495 23.242 10.1047 21.5817 8.8064 20.8323ZM1.35768 53.57L6.09179 50.8369C7.39046 50.0868 9.05035 50.5322 9.79995 51.8303C10.5495 53.1288 10.1047 54.7889 8.8064 55.5385L4.0723 58.2717C2.78014 59.018 1.11753 58.583 0.364134 57.2782C-0.385462 55.9797 0.0593732 54.3196 1.35768 53.57ZM60.7755 25.534L65.5096 22.8008C66.8079 22.0512 67.2527 20.3911 66.5031 19.0926C65.7535 17.7943 64.0936 17.3491 62.795 18.0991L58.0609 20.8323C56.7626 21.5819 56.3177 23.242 57.0673 24.5405C57.8207 25.8453 59.4833 26.2804 60.7755 25.534ZM53.2537 29.3185C61.5463 29.2827 69.0842 34.3601 72.1917 42.0392C83.132 41.1823 92.6588 49.839 92.6588 60.9883C92.6588 70.7515 85.2955 78.7987 75.8076 79.8688C75.7582 79.8841 75.7224 79.8978 75.6861 79.9099C75.1755 80.0802 74.5644 79.9489 34.6888 79.9904C22.6473 79.9904 15.3287 66.659 21.7654 56.4904C15.7287 52.6287 11.7168 45.8679 11.7168 38.1854C11.7168 26.2107 21.4588 16.4685 33.4335 16.4685C42.2512 16.4685 49.8555 21.7522 53.2537 29.3185ZM33.4337 21.8979C24.4527 21.8979 17.146 29.2045 17.146 38.1856C17.146 44.2996 20.5337 49.6356 25.5295 52.4206C27.7193 50.7739 30.2618 49.7665 32.9583 49.4706C33.0539 40.4856 39.0099 32.6543 47.5879 30.1407C44.7817 25.2224 39.49 21.8979 33.4337 21.8979ZM50.8659 74.5605C62.6887 74.5642 74.128 74.5677 74.7807 74.5147C81.7615 73.9437 87.2296 68.0021 87.2296 60.9883C87.2296 52.415 79.3531 45.947 70.892 47.6968C69.4878 47.9866 68.0992 47.1347 67.7227 45.7505C65.9619 39.2718 60.0371 34.747 53.315 34.747C44.2283 34.747 37.2729 42.789 38.5314 51.7709C38.773 53.4951 37.3471 55.0089 35.5973 54.8511C32.6966 54.5873 30.0347 55.5585 28.0356 57.384C21.3756 63.4659 25.7376 74.5614 34.6888 74.5614C39.3969 74.5569 45.1758 74.5587 50.8659 74.5605Z"
						/>
						<defs>
							<linearGradient
								id="gradient"
								x1="0.958316"
								y1="-5.72104"
								x2="87.4502"
								y2="70.2494"
								gradientUnits="userSpaceOnUse"
							>
								<stop stop-color="#0161CD" />
								<stop offset="1" stop-color="#8080F1" />
							</linearGradient>
						</defs>
					</svg>
				</div>
			</div>
			<div class="relative mb-4 h-10 w-full sm:mb-0">
				<p class="magic-wand absolute left-0 top-0 w-full text-center text-lg font-semibold text-gray-800">
					{{ trans('Putting a spell on it to create something unique') }}
				</p>
				<p class="typewriter absolute left-0 top-0 w-full text-center text-lg font-semibold text-gray-800">
					{{ trans('Writing incredible texts at the speed of light') }}
				</p>
				<p class="gallery absolute left-0 top-0 w-full text-center text-lg font-semibold text-gray-800">
					{{ trans('On a mission to find the best images') }}
				</p>
			</div>
			<div class="bg-gray-150 relative mx-auto mt-5 flex w-9/12 max-w-lg items-center rounded-lg px-8 py-4">
				<div class="text-darkblue-400 text-center text-sm font-semibold">
					{{ trans('While you wait, ') }}
					<div class="inline-block text-blue-500 underline hover:cursor-pointer" @click="relocate()">
						{{ trans('check out this AI guide ') }}
					</div>
					{{ trans('with tips - and prompts - to boost your presentations.') }}
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.ai-loading
    .spinner
        stroke-linecap: round
        stroke-width: 3px
        r: 44px
        animation: rotate 3s linear infinite
        transform: scale(0.9)

        #circle-animate
            fill: none
            stroke-width: 2
            stroke-dasharray: 300
            stroke-dashoffset: 0
            transform-origin: center
            animation: dash 3s ease-in-out infinite

            @keyframes rotate
                0%
                    transform: scale(0.9) rotate(0deg)

                100%
                    transform: scale(0.9) rotate(360deg)

            @keyframes dash
                0%
                    stroke-dashoffset: 300

                50%
                    stroke-dashoffset: 6
                    transform: rotate(100deg)

                100%
                    stroke-dashoffset: 300
                    transform: rotate(450deg)

    .magic-wand,
    .typewriter,
    .gallery
        animation: carousel 12s infinite
        opacity: 0

        @keyframes carousel
            0%
                opacity: 0

            5%

                opacity: 1

            28%
                opacity: 1

            33%
                opacity: 0

    .typewriter
        animation-delay: 4s

    .gallery
        animation-delay: 8s

    .wand
        animation: wand 4s infinite

        @keyframes wand
            50%
                transform: rotate(-4deg)

            100%
                transform: rotate(0deg)
    .star
        animation: star 2s infinite
        transform-origin: 50% 50%

        &--1
            animation-delay: 0.5s

        &--2
            animation-delay: 0.8s

        &--3
            animation-delay: 1s

        &--4
            animation-delay: 1.2s

        @keyframes star
            0%
                transform: scale(1.05)

            20%
                transform: scale(0.9)
                opacity: 0

            80%
                transform: scale(0.9)
                opacity: 0

            100%
                transform: scale(1.05)

    .papper
        animation: papper 4s infinite steps(8)

        @keyframes papper
            0%
                transform: translateY(0)

            100%
                transform: translateY(-16px)

    .ribbon
        animation: ribbon 4s infinite steps(8)

        @keyframes ribbon
            0%
                transform: translateX(5px)

            100%
                transform: translateX(-5px)

    .image
        transform: translateX(-20px)
        transform-origin: 50% 50%
        animation: image 3s infinite ease-in-out
        opacity: 0

        &--1
            animation-delay: 1s

        &--2
            animation-delay: 2s

        @keyframes image
            0%
                opacity: 0
                transform: translateX(40px) scale(1)

            15%
                opacity: 1
                transform: scale(1.2)

            30%
                opacity: 1
                transform: scale(1.2)

            40%
                transform: translateX(-40px) scale(1)
                opacity: 0



.bg-noise
    background: linear-gradient(0deg, rgba(15, 32, 66,0.9), rgba(2, 2, 86, 0.9)),url(https://freepik.cdnpk.net/assets/964f58b6a12e1b2e627e8d505958023b.png)

.light-ellipse
    animation: light-ellipse 10s infinite

    @keyframes light-ellipse
        0%
            background-color: #5A3FFF
            transform: translate(-50%, -50%)

        25%
            background-color: #E34DC2
            transform: translate(-75%, -75%)

        50%
            background-color: #62A8FA
            transform: translate(-65%, -25%)

        75%
            background-color: #34BEA7
            transform: translate(-5%, -35%)

        100%
            background-color: #5A3FFF
            transform: translate(-50%, -50%)

.magic-wand
    .wand
        animation: wand 2s infinite

        @keyframes wand
            0%
                transform: rotate(0deg)

            6%
                transform: rotate(5deg)

            12%
                transform: rotate(0deg)

            18%
                transform: rotate(5deg)

            25%
                transform: rotate(0deg)
    .star
        @apply w-1 h-1 absolute opacity-50
        top: 3px
        right: 2px

        &--1
            animation: magic-star 2s infinite

            @keyframes magic-star
                0%
                    top: 3px
                    right: 2px

                12%
                    top: 15px
                    right: -5px
                    opacity: 0.5

                25%
                    top: 15px
                    right: -5px
                    opacity: 0

                50%
                    opacity: 0

                100%
                    opacity: 0

        &--2
            animation: magic-star2 2s infinite
            animation-delay: 0.05s

            @keyframes magic-star2
                0%
                    top: 3px
                    right: 2px

                12%
                    top: 15px
                    right: 5px
                    opacity: 0.5

                25%
                    top: 15px
                    right: 5px
                    opacity: 0

                50%
                    opacity: 0

                100%
                    opacity: 0

        &--3
            animation: magic-star3 2s infinite
            animation-delay: 0.1s

            @keyframes magic-star3
                0%
                    top: 3px
                    right: 2px

                12%
                    top: 3px
                    right: -10px
                    opacity: 0.5

                25%
                    top: 3px
                    right: -10px
                    opacity: 0

                50%
                    opacity: 0

                100%
                    opacity: 0

        &--4
            animation: magic-star4 2s infinite
            animation-delay: 0.15s

            @keyframes magic-star4
                0%
                    top: 3px
                    right: 2px

                12%
                    top: -5px
                    right: 2px
                    opacity: 0.5

                25%
                    top: -5px
                    right: 2px
                    opacity: 0

                50%
                    opacity: 0

                100%
                    opacity: 0

.animate-star
    animation: animate-star 2s infinite
    @keyframes animate-star
        0%
            opacity: 1

        75%, 100%
            transform: scale(2.5)
            opacity: 0

    &:nth-child(1)
        animation-delay: 0.3s
    &:nth-child(4)
        animation-delay: 0.6s
    &:nth-child(2)
        animation-delay: 0.9s
    &:nth-child(5)
        animation-delay: 1.2s
    &:nth-child(3)
        animation-delay: 1.5s

.arrow-bounce
    animation: bounce 2s infinite ease

    @keyframes bounce
        0%
            transform: translateY(0)

        60%
            transform: translateY(0)

        80%
            transform: translateY(8px)

        100%
            transform: translateY(0)
</style>
